import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Chip, Container, Rating, Tabs, TabsContent, TabsList, TabsTrigger } from "rothzerg-ui";
import { ProductCarousel } from "@/components/catalog/ProductCarousel";
import {
  brandChips,
  circularCategories,
  communityPhotos,
  familyContentCards,
  featureCategoryCards,
  homeAppBanner,
  homeHero,
  lifestyleCards,
  promoBanners,
  serviceIcons,
  topPicksTabs,
} from "@/data/home";
import { getHomeTopPicks } from "@/data/selectors";

const serviceIconSvg: Record<string, string> = {
  cnc: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  delivery: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`,
  returns: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>`,
  prices: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
};

export function HomePage() {
  const [activeTab, setActiveTab] = useState(topPicksTabs[0]?.id ?? "womens");

  return (
    <div className="hp">
      {/* ── Hero ── */}
      <section className="hp-hero">
        <div className="hp-hero__img-wrap">
          <img src={homeHero.image} alt="" className="hp-hero__img" />
          <div className="hp-hero__overlay" />
        </div>
        <Container>
          <div className="hp-hero__content">
            <span className="hp-hero__eyebrow">{homeHero.eyebrow}</span>
            <h1 className="hp-hero__title">{homeHero.title}</h1>
            <p className="hp-hero__desc">{homeHero.description}</p>
            <Link to={homeHero.ctaHref}>
              <Button size="lg">{homeHero.ctaLabel}</Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* ── Value props strip ── */}
      <div className="hp-value-strip">
        <Container>
          <div className="hp-value-strip__inner">
            {serviceIcons.map((icon) => (
              <div key={icon.id} className="hp-value-item">
                <span
                  className="hp-value-item__icon"
                  dangerouslySetInnerHTML={{ __html: serviceIconSvg[icon.id] ?? "" }}
                />
                <div>
                  <strong className="hp-value-item__label">{icon.label}</strong>
                  <span className="hp-value-item__desc">{icon.description}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </div>

      {/* ── Category nav ── */}
      <Container>
        <nav className="hp-cat-nav" aria-label="Shop by category">
          {circularCategories.map((cat) => (
            <Link key={cat.id} to={cat.href} className="hp-cat-nav__item">
              <div className="hp-cat-nav__img-wrap">
                <img src={cat.image} alt="" className="hp-cat-nav__img" />
              </div>
              <span className="hp-cat-nav__label">{cat.label}</span>
            </Link>
          ))}
        </nav>
      </Container>

      {/* ── Shop by room ── */}
      <section className="hp-section">
        <Container>
          <div className="hp-section__head">
            <h2 className="hp-section__title">Refresh your space</h2>
            <Link to="/categories/home-living" className="hp-section__see-all">See all</Link>
          </div>
          <div className="hp-room-grid">
            {lifestyleCards.map((card) => (
              <Link key={card.id} to={card.href} className="hp-room-card">
                <img src={card.image} alt={card.title} className="hp-room-card__img" />
                <div className="hp-room-card__overlay">
                  <span className="hp-room-card__label">{card.title}</span>
                  <span className="hp-room-card__cta">Shop now →</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Top picks ── */}
      <section className="hp-section hp-section--muted">
        <Container>
          <div className="hp-section__head">
            <h2 className="hp-section__title">Top picks · everyday low prices</h2>
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList variant="underline">
              {topPicksTabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
            {topPicksTabs.map((tab) => (
              <TabsContent key={tab.id} value={tab.id}>
                <div className="hp-product-scroll">
                  {getHomeTopPicks(tab.id).map((product) => (
                    <div key={product.id} className="hp-product-card">
                      <div className="hp-product-card__img-wrap">
                        <img src={product.image} alt={product.title} className="hp-product-card__img" />
                      </div>
                      <div className="hp-product-card__body">
                        <strong className="hp-product-card__price">${product.price}</strong>
                        {product.wasPrice && product.wasPrice > product.price && (
                          <span className="hp-product-card__was">${product.wasPrice}</span>
                        )}
                        <span className="hp-product-card__title">{product.title}</span>
                        {product.rating != null && (
                          <Rating value={product.rating} readOnly size="sm" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </Container>
      </section>

      {/* ── Feature categories (clothing) ── */}
      <section className="hp-section">
        <Container>
          <div className="hp-feat-grid">
            {featureCategoryCards.map((card) => (
              <Link key={card.id} to={card.href} className="hp-feat-card">
                <img src={card.image} alt={card.label} className="hp-feat-card__img" />
                <div className="hp-feat-card__footer">
                  <span className="hp-feat-card__label">{card.label}</span>
                  <span className="hp-feat-card__arrow">→</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Promo banners ── */}
      <section className="hp-section">
        <Container>
          <div className="hp-promo-grid">
            {promoBanners.map((banner) => (
              <Link key={banner.id} to={banner.href} className="hp-promo-banner">
                <img src={banner.image} alt={banner.title} className="hp-promo-banner__img" />
                <div className="hp-promo-banner__content">
                  <strong className="hp-promo-banner__title">{banner.title}</strong>
                  <span className="hp-promo-banner__sub">{banner.subtitle}</span>
                  <span className="hp-promo-banner__cta">Shop now</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Brands ── */}
      <div className="hp-brands-strip">
        <Container>
          <div className="hp-brands-strip__inner">
            <span className="hp-brands-strip__heading">Shop by brand</span>
            <div className="hp-brands-chips">
              {brandChips.map((brand) => (
                <Chip key={brand} label={brand} variant="outline" />
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* ── Family moments ── */}
      <section className="hp-section hp-section--muted">
        <Container>
          <div className="hp-section__head">
            <h2 className="hp-section__title">Family moments</h2>
          </div>
          <div className="hp-family-grid">
            {familyContentCards.map((card) => (
              <Link key={card.id} to={card.href} className="hp-family-card">
                <div className="hp-family-card__img-wrap">
                  <img src={card.image} alt={card.title} className="hp-family-card__img" />
                </div>
                <div className="hp-family-card__body">
                  <strong className="hp-family-card__title">{card.title}</strong>
                  <p className="hp-family-card__desc">{card.description}</p>
                  <span className="hp-family-card__link">Explore →</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* ── App banner ── */}
      <div className="hp-app-banner">
        <Container>
          <div className="hp-app-banner__inner">
            <div>
              <strong className="hp-app-banner__title">{homeAppBanner.message}</strong>
            </div>
            <Button variant="outline" size="sm">{homeAppBanner.linkText}</Button>
          </div>
        </Container>
      </div>

      {/* ── Community UGC ── */}
      <section className="hp-section">
        <Container>
          <div className="hp-section__head">
            <h2 className="hp-section__title">Shop from our community</h2>
          </div>
          <div className="hp-community-grid">
            {communityPhotos.map((photo) => (
              <div key={photo.id} className="hp-community-photo">
                <img src={photo.image} alt={photo.alt} />
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Recommended ── */}
      <ProductCarousel
        id="home-recommended"
        title="Recommended for you"
        products={getHomeTopPicks(activeTab)}
      />
    </div>
  );
}
